import useDocumentImagesUpload, {
  base64ToFile,
} from "features/shared/hooks/useDocumentImagesUpload";
import useMutatedPagePath from "features/shared/hooks/analytics/useMutatedPagePath";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import usePaxSession from "features/shared/hooks/usePaxSession";
import Logging from "logging";
import { ChangeEvent, useCallback } from "react";
import { DocProcessingStatus } from "models/doc-processing-status";
import { useAppStore } from "store/store";
import { PaxCheckDocumentProvisionMethod } from "api";
import useItemLevelNavigationState from "features/shared/hooks/navigation/useItemLevelNavigationState";
import { useSearchParams } from "react-router-dom";
import { getGeneralAnalyticsParams } from "./analytics/useGeneralAnalytics";
import { getPaxLevelParams } from "./analytics/usePaxLevelAnalytics";
import AnalyticsService from "services/analytics/analytics";
import useDemoScenario from "./demo/useDemoScenario";

const useUploadSingleSideDocument = (
  processingMayTakeLong: boolean | null,
  additionalPaxSessionIds?: string[],
  pageIndexToProcess?: number
): [(e: ChangeEvent<HTMLInputElement>) => void] => {
  const locale = useAppStore((state) => state.locale);

  const paxSession = usePaxSession();
  const paxSessionId = paxSession?.paxSessionId || null;

  const [path, title, location] = useMutatedPagePath();
  const paxSessions = useAppStore((state) => state.paxSessions);
  const journey = useAppStore((state) => state.journey);

  const [searchParams] = useSearchParams();
  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  const navigate = useNavigateWithQuery();

  const setProcessingStatus = useAppStore((state) => state.setProcessingStatus);

  const setProcessedFileName = useAppStore(
    (state) => state.setProcessedFileName
  );
  const setProcessingMayTakeLong = useAppStore(
    (state) => state.setProcessingMayTakeLong
  );

  const uploadFiles = useDocumentImagesUpload(
    PaxCheckDocumentProvisionMethod.UPLOAD
  );

  const demoScenario = useDemoScenario(
    itemLevelNavigationState.forceDocumentType ??
      itemLevelNavigationState.itemType
  );

  const onFileSelected = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const fileInput = e.target;

      if (!(fileInput && fileInput.files && fileInput.files.length > 0)) {
        Logging.log(
          "uploading",
          paxSessionId || "missing",
          "File input is empty"
        );
        return;
      }

      const file =
        demoScenario !== undefined
          ? base64ToFile(
              demoScenario.fileContentBase64,
              demoScenario.fileName,
              "image/jpeg"
            )
          : fileInput.files[0];

      AnalyticsService.shared.trackEvent(
        "upload_file_selected",
        {
          ...getGeneralAnalyticsParams(
            paxSessions,
            journey,
            path,
            title,
            location,
            locale
          ),
          ...getPaxLevelParams(paxSessions, paxSessionId),
          item_type: itemLevelNavigationState.itemType,
        },
        false // isScanRelated
      );

      setProcessingStatus(DocProcessingStatus.InProgress, null);
      setProcessedFileName(file.name);
      setProcessingMayTakeLong(processingMayTakeLong);

      navigate("../processing");

      uploadFiles([file], additionalPaxSessionIds, pageIndexToProcess);
    },
    [
      itemLevelNavigationState,
      location,
      navigate,
      path,
      paxSessionId,
      setProcessedFileName,
      processingMayTakeLong,
      setProcessingMayTakeLong,
      setProcessingStatus,
      title,
      uploadFiles,
      journey,
      locale,
      paxSessions,
      additionalPaxSessionIds,
      pageIndexToProcess,
      demoScenario,
    ]
  );

  return [onFileSelected];
};

export default useUploadSingleSideDocument;
